import React, { useMemo, useState } from 'react';
import { Box, Grid, List, ListItem, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';

import { ReadFile } from '@paragon/client-tools';
import { LayoutModel } from '@paragon/contract-library';
import { getLayoutModelsWithPendingChanges } from '@paragon/layout-models';
import { InlineNotification } from '@paragon/notification-tools';
import { closeCurrentPopup, Dialog } from '@paragon/popup-state';
import { getProductionGroupName } from '@paragon/production-app-utilities';
import { assertExhaustiveSwitch } from '@paragon/utilities';

import { createProductionGroup, createProductionGroupFromCnt } from '../../store/actions/productionGroups';
import { getScopeComponentDesignGuids, getScopeProjectGuids, ProjectActionScope } from '../../store/projectActionScope';
import { getDoWorkingChangesExistByLoadedComponentDesignGuid } from '../../store/selectors/getDoWorkingChangesExistByComponentDesignGuid';
import { getProductionGroupsByProjectGuid } from '../../store/selectors/getProductionGroupsByProjectGuid';
import { useAppDispatch, useAppSelector } from '../hooks';
import { ChooseQuantities, QuantityOption, useScopedQuantitiesState } from './ChooseQuantities';
import { CountFileUploader } from './CountFileUploader';
import {
  getProductionGroupPropertiesAreValid,
  ProductionGroupProperties,
  SettableProductionGroupProperties,
} from './ProductionGroupProperties';

const projectShortNameCharacterLimit = 20;

function getInitialProductionGroupProperties(projectLayoutModel: LayoutModel): SettableProductionGroupProperties {
  // If the project starts with a number, parse that into the project ID
  const projectIdAtStart = projectLayoutModel.name.match(/^(\d*)\s?-?\s?/);
  const hasProjectIdAtStart = projectIdAtStart && projectIdAtStart.length > 0;
  const projectId = hasProjectIdAtStart ? projectIdAtStart[1] : '';
  const projectShortName = hasProjectIdAtStart
    ? projectLayoutModel.name
        .substring(projectIdAtStart[0].length, projectIdAtStart[0].length + projectShortNameCharacterLimit)
        .trim()
    : projectLayoutModel.name.substring(0, projectShortNameCharacterLimit);
  return {
    projectId,
    projectShortName,
    buildingType: '',
    buildingInstance: '',
    level: '',
  };
}

export function productionGroupPropertiesEquals(
  productionGroupOne: SettableProductionGroupProperties,
  productionGroupTwo: SettableProductionGroupProperties,
) {
  return (
    productionGroupOne.projectId === productionGroupTwo.projectId &&
    productionGroupOne.projectShortName === productionGroupTwo.projectShortName &&
    productionGroupOne.buildingType === productionGroupTwo.buildingType &&
    productionGroupOne.buildingInstance === productionGroupTwo.buildingInstance &&
    productionGroupOne.level === productionGroupTwo.level
  );
}

export function CreateProductionGroupDialog({ scope }: { readonly scope: ProjectActionScope }) {
  const dispatch = useAppDispatch();
  const layoutModels = useAppSelector(getLayoutModelsWithPendingChanges);
  const productionGroupsByProject = useAppSelector(getProductionGroupsByProjectGuid);

  const componentDesignGuids = getScopeComponentDesignGuids(scope);
  const doWorkingChangesExistByComponentDesignGuid = useAppSelector(
    getDoWorkingChangesExistByLoadedComponentDesignGuid,
  );

  const projectGuid = getScopeProjectGuids(scope)[0];

  const [productionGroupProperties, setProductionGroupProperties] = useState<SettableProductionGroupProperties>(
    getInitialProductionGroupProperties(layoutModels[projectGuid]),
  );
  const [creationMethod, setCreationMethod] = useState<'uploadCntFile' | 'enterQuantities'>('uploadCntFile');
  const [quantityOption, setQuantityOption] = useState(QuantityOption.OneOfTopLevel);

  const [enterQuantitiesQuantityValues, setEnterQuantitiesQuantityValues] = useScopedQuantitiesState(scope);
  const [uploadFilesQuantityValues, setUploadFilesQuantityValues] = useState(() =>
    componentDesignGuids.reduce(
      (quantityMap, componentDesignGuid) => {
        quantityMap[componentDesignGuid] = 0;
        return quantityMap;
      },
      {} as { [guid: string]: number },
    ),
  );
  const [uploadFilesSourceCnt, setUploadFilesSourceCnt] = useState<ReadFile | null>(null);
  const [uploadFilesError, setUploadFilesError] = useState<boolean>(false);

  const componentDesignGuidsWithWorkingChanges = useMemo(() => {
    let componentDesignQuantities: { [guid: string]: number };
    switch (creationMethod) {
      case 'uploadCntFile':
        componentDesignQuantities = uploadFilesQuantityValues;
        break;
      case 'enterQuantities':
        componentDesignQuantities = enterQuantitiesQuantityValues;
        break;
      default:
        assertExhaustiveSwitch(creationMethod);
    }
    return Object.entries(componentDesignQuantities)
      .filter(
        ([componentDesignGuid, quantity]) =>
          quantity > 0 && doWorkingChangesExistByComponentDesignGuid[componentDesignGuid],
      )
      .map(([componentDesignGuid]) => componentDesignGuid);
  }, [
    creationMethod,
    uploadFilesQuantityValues,
    enterQuantitiesQuantityValues,
    doWorkingChangesExistByComponentDesignGuid,
  ]);

  const productionGroupsForActiveProject = productionGroupsByProject[projectGuid] ?? [];
  const productionGroupWithSamePropertiesExists = productionGroupsForActiveProject.some(
    (productionGroupForActiveProject) =>
      productionGroupPropertiesEquals(productionGroupForActiveProject, productionGroupProperties),
  );

  return (
    <Dialog
      title="Create Production Group"
      open
      submitText="Create"
      onSubmit={() => {
        switch (creationMethod) {
          case 'uploadCntFile':
            if (uploadFilesSourceCnt === null) throw new Error('CNT file not provided');
            dispatch(createProductionGroupFromCnt(projectGuid, productionGroupProperties, uploadFilesSourceCnt));
            break;
          case 'enterQuantities':
            dispatch(createProductionGroup(projectGuid, productionGroupProperties, enterQuantitiesQuantityValues));
            break;
          default:
            assertExhaustiveSwitch(creationMethod);
        }
        dispatch(closeCurrentPopup());
      }}
      onCancel={() => void dispatch(closeCurrentPopup())}
      contentSx={{ width: '800px', height: '700px' }}
      submitDisabled={
        productionGroupWithSamePropertiesExists ||
        !getProductionGroupPropertiesAreValid(productionGroupProperties) ||
        componentDesignGuidsWithWorkingChanges.length > 0 ||
        (creationMethod === 'uploadCntFile' && (uploadFilesSourceCnt === null || uploadFilesError === true))
      }
      enterKeySubmits
    >
      <Grid container spacing={1}>
        <Grid size={6}>
          <Typography variant="h6">Properties</Typography>
          <ProductionGroupProperties
            productionGroupProperties={productionGroupProperties}
            submit={setProductionGroupProperties}
          />

          {productionGroupWithSamePropertiesExists && (
            <InlineNotification type="error" sx={{ mb: 2 }}>
              A production group with the same properties already exists in this project. Use different properties to
              avoid ambiguity.
            </InlineNotification>
          )}

          <Typography>Existing Production Groups:</Typography>
          <List>
            {productionGroupsForActiveProject.map((productionGroup) => (
              <ListItem key={productionGroup.guid}>{getProductionGroupName(productionGroup, true)}</ListItem>
            ))}
          </List>
        </Grid>

        <Grid size={6}>
          <Typography variant="h6">Quantities</Typography>
          <Typography sx={{ mb: 2 }}>
            The production group is created with the truss as it is at the most recent analysis for each selected truss.
            Future changes to each truss will not propagate to the production group automatically. You must use the
            &quot;Add to Production Group&quot; option to push updated versions.
          </Typography>
          <ToggleButtonGroup
            value={creationMethod}
            exclusive
            onChange={(_, value) => {
              if (value) setCreationMethod(value);
            }}
            sx={{ mb: 1 }}
          >
            <ToggleButton value="uploadCntFile">Upload CNT File</ToggleButton>
            <ToggleButton value="enterQuantities">Enter Quantities</ToggleButton>
          </ToggleButtonGroup>
          <Box sx={{ mb: 2 }}>
            {creationMethod === 'uploadCntFile' && (
              <CountFileUploader
                quantities={uploadFilesQuantityValues}
                onQuantityChange={(quantities, sourceFile, fileContainsOtherComponents) => {
                  setUploadFilesQuantityValues(quantities);
                  setUploadFilesSourceCnt(sourceFile);
                  setUploadFilesError(fileContainsOtherComponents);
                }}
                acceptCntOnly
              />
            )}
            {creationMethod === 'enterQuantities' && (
              <ChooseQuantities
                scope={scope}
                quantityOption={quantityOption}
                quantities={enterQuantitiesQuantityValues}
                onQuantityChange={(quantityOption, quantities) => {
                  setQuantityOption(quantityOption);
                  setEnterQuantitiesQuantityValues(quantities);
                }}
              />
            )}
          </Box>
          {creationMethod === 'uploadCntFile' &&
            componentDesignGuidsWithWorkingChanges.length === 0 &&
            uploadFilesSourceCnt !== null && <InlineNotification type="info">CNT file selected</InlineNotification>}
          {componentDesignGuidsWithWorkingChanges.length > 0 && (
            <InlineNotification type="error">
              Components have working changes:{' '}
              {componentDesignGuidsWithWorkingChanges
                .map((componentDesignGuid) => layoutModels[componentDesignGuid].name)
                .join(', ')}
              . Analyze them first, then return here and refresh.
            </InlineNotification>
          )}
        </Grid>
      </Grid>
    </Dialog>
  );
}
